import { CounterPillModel, IndicatorBoxType, IndicatorCardModel, TotalPriceDataType, VariationPriceDataType } from './BigNumbersArea.types';

export const PROJECTION_DAYS = 28;

export const EMPTY_VALUE = '—';

export const PROJECTION_PERIOD_TOOLTIP = [
  'Projetamos volume, receita e lucro multiplicando a média diária de vendas dos últimos 28 dias por este número de dias.',
  'Margem e CPI não são afetados, por serem percentuais.',
].join(' ');

// Margem, volume, receita e lucro saem do mesmo conjunto de produtos: só os que têm custo cadastrado.
const ONLY_WITH_COST = 'Considera só os produtos com custo cadastrado.';

export const INDICATOR_TOOLTIPS = {
  margin: `Calculamos a margem em base líquida, descontando custo, impostos e despesas do preço final. ${ONLY_WITH_COST}`,
  cpi: 'Calculamos o CPI de cada produto comparando o seu preço com o da concorrência, e ponderamos os índices pelo volume de vendas.',
  salesVolume: `Projetamos o volume a partir da média diária de vendas a preço regular. ${ONLY_WITH_COST}`,
  revenue: `Receita bruta: multiplicamos o preço pelo volume, sem descontar impostos e despesas. ${ONLY_WITH_COST}`,
  profit: `Calculamos o lucro em base líquida, descontando custo, impostos e despesas. ${ONLY_WITH_COST}`,
  profitDifference: [
    'Mostramos quanto o lucro muda se você aplicar os preços sugeridos.',
    'É a diferença entre o lucro projetado e o lucro atual, os dois em base líquida.',
    ONLY_WITH_COST,
  ].join(' '),
};

const INDICATOR_LABELS = {
  margin: 'Margem projetada',
  cpi: 'CPI projetada',
  salesVolume: 'Volume de vendas projetado',
  revenue: 'Receita projetada',
  profit: 'Lucro projetado',
  profitDifference: 'Estimativa de diferença no lucro',
};

const roundTo = (value: number, digits: number) => Number(value.toFixed(digits)) || 0;

const formatDecimal = (value: number, digits: number) =>
  roundTo(value, digits).toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const abbreviate = (absoluteValue: number) => {
  if (roundTo(absoluteValue / 1000, 1) >= 1000) return `${formatDecimal(absoluteValue / 1000000, 2)} M`;
  if (absoluteValue >= 1000) return `${formatDecimal(absoluteValue / 1000, 1)} mil`;
  return null;
};

const getSign = (value: number, signed: boolean) => {
  if (value < 0) return '-';
  if (signed && value > 0) return '+';
  return '';
};

export const formatCurrencyValue = (value: number, signed = false) => {
  const absoluteValue = roundTo(Math.abs(value), 2);
  const sign = absoluteValue === 0 ? '' : getSign(value, signed);
  return `${sign}R$ ${abbreviate(absoluteValue) ?? formatDecimal(absoluteValue, 2)}`;
};

export const formatVolume = (value: number) => {
  const absoluteValue = roundTo(Math.abs(value), 0);
  const sign = absoluteValue === 0 ? '' : getSign(value, false);
  return `${sign}${abbreviate(absoluteValue) ?? formatDecimal(absoluteValue, 0)}`;
};

export const formatPercent = (fraction: number) => `${formatDecimal(fraction * 100, 1)}%`;

export const formatCount = (value: number) => formatDecimal(value, 0);

export const getPercentPointVariation = (newFraction: number, currentFraction: number) =>
  roundTo(roundTo(newFraction * 100, 1) - roundTo(currentFraction * 100, 1), 1);

export const getRelativeVariation = (projected: number, current: number) => {
  if (current === 0) return undefined;
  const variation = ((projected - current) / Math.abs(current)) * 100;
  if (!Number.isFinite(variation)) return undefined;
  return roundTo(variation, 1);
};

export const getDifferenceBoxType = (difference: number): IndicatorBoxType => {
  const rounded = roundTo(difference, 2);
  if (rounded > 0) return 'up';
  if (rounded < 0) return 'down';
  return 'container';
};

const buildPercentPointVariation = (newFraction: number, currentFraction: number) => {
  const value = getPercentPointVariation(newFraction, currentFraction);
  return value === 0 ? undefined : { value, percentPoint: true };
};

const buildRelativeVariation = (projected: number, current: number) => {
  const value = getRelativeVariation(projected, current);
  return value === undefined || value === 0 ? undefined : { value, percentPoint: false };
};

const buildEmptyIndicatorCards = (): IndicatorCardModel[] =>
  (['margin', 'cpi', 'salesVolume', 'revenue', 'profit', 'profitDifference'] as const).map(id => ({
    id,
    label: INDICATOR_LABELS[id],
    tooltip: INDICATOR_TOOLTIPS[id],
    value: EMPTY_VALUE,
    current: ['salesVolume', 'profitDifference'].includes(id) ? undefined : EMPTY_VALUE,
    type: 'container',
  }));

const toNumber = (value?: number | null) => Number(value ?? 0);

const normalizeVariationPrice = (data: VariationPriceDataType): VariationPriceDataType => ({
  marginCurrent: toNumber(data.marginCurrent),
  marginNew: toNumber(data.marginNew),
  cpiCurrent: toNumber(data.cpiCurrent),
  cpiNew: toNumber(data.cpiNew),
  profitCurrent: toNumber(data.profitCurrent),
  profitNew: toNumber(data.profitNew),
  salesVolume: toNumber(data.salesVolume),
  revenueCurrent: toNumber(data.revenueCurrent),
  revenueNew: toNumber(data.revenueNew),
});

export const buildIndicatorCards = (rawData?: VariationPriceDataType, days = PROJECTION_DAYS): IndicatorCardModel[] => {
  if (!rawData) return buildEmptyIndicatorCards();

  const data = normalizeVariationPrice(rawData);

  const profitProjected = data.profitNew * days;
  const profitCurrent = data.profitCurrent * days;
  const profitDifference = profitProjected - profitCurrent;
  const salesVolume = data.salesVolume * days;
  const revenueProjected = data.revenueNew * days;
  const revenueCurrent = data.revenueCurrent * days;

  return [
    {
      id: 'margin',
      label: INDICATOR_LABELS.margin,
      tooltip: INDICATOR_TOOLTIPS.margin,
      value: formatPercent(data.marginNew),
      variation: buildPercentPointVariation(data.marginNew, data.marginCurrent),
      current: formatPercent(data.marginCurrent),
      type: 'container',
    },
    {
      id: 'cpi',
      label: INDICATOR_LABELS.cpi,
      tooltip: INDICATOR_TOOLTIPS.cpi,
      value: formatPercent(data.cpiNew),
      variation: buildPercentPointVariation(data.cpiNew, data.cpiCurrent),
      current: formatPercent(data.cpiCurrent),
      type: 'container',
    },
    {
      id: 'salesVolume',
      label: INDICATOR_LABELS.salesVolume,
      tooltip: INDICATOR_TOOLTIPS.salesVolume,
      value: formatVolume(salesVolume),
      type: 'container',
    },
    {
      id: 'revenue',
      label: INDICATOR_LABELS.revenue,
      tooltip: INDICATOR_TOOLTIPS.revenue,
      value: formatCurrencyValue(revenueProjected),
      variation: buildRelativeVariation(revenueProjected, revenueCurrent),
      current: formatCurrencyValue(revenueCurrent),
      type: 'container',
    },
    {
      id: 'profit',
      label: INDICATOR_LABELS.profit,
      tooltip: INDICATOR_TOOLTIPS.profit,
      value: formatCurrencyValue(profitProjected),
      variation: buildRelativeVariation(profitProjected, profitCurrent),
      current: formatCurrencyValue(profitCurrent),
      type: 'container',
    },
    {
      id: 'profitDifference',
      label: INDICATOR_LABELS.profitDifference,
      tooltip: INDICATOR_TOOLTIPS.profitDifference,
      value: formatCurrencyValue(profitDifference, true),
      type: getDifferenceBoxType(profitDifference),
    },
  ];
};

const buildCounterPair = (data: TotalPriceDataType | undefined, filtered: keyof TotalPriceDataType, total: keyof TotalPriceDataType) =>
  data ? { value: formatCount(toNumber(data[filtered])), total: formatCount(toNumber(data[total])) } : { value: EMPTY_VALUE };

export const buildCounterPills = (data?: TotalPriceDataType): CounterPillModel[] => [
  { id: 'totalPrices', label: 'Total de preços', value: data ? formatCount(toNumber(data.totalPricesFiltered)) : EMPTY_VALUE },
  { id: 'newCosts', label: 'Novos custos', ...buildCounterPair(data, 'totalNewCostsFiltered', 'totalNewCosts') },
  { id: 'suggestedPrices', label: 'Preços sugeridos', ...buildCounterPair(data, 'totalSugestedFiltered', 'totalSugestedPrices') },
  { id: 'appliedPrices', label: 'Preços aplicados', ...buildCounterPair(data, 'totalAppliedFiltered', 'totalAppliedPrices') },
];
